import { BreadcrumbBasketDataDocument } from '@packages/gql/generated/shopping/BreadcrumbBasketDataDocument';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type {
  AddToBasketMutation,
  ChangeProductMutation,
  RawProductQuery,
} from '@packages/gql/generated/shopping/graphql';
import { TrackGlycerinAddToCartLineItemFragmentDoc } from '@packages/gql/generated/shopping/TrackGlycerinAddToCartLineItemFragmentDoc';
import type {
  GTMEventGlycerinAddToCart,
  GTMEventGlycerinUpdateCart,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { type ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { selectedQuantity } from '../../../atoms';
import { getSkuPart } from '../../../helpers/getSkuPart/getSkuPart';
import { useHashParameters } from '../../../hooks/useHashParameters/useHashParameters';
import { getGlycerinArticleId } from '../../helper/getGlycerinArticleId/getGlycerinArticleId';
import { getGlycerinAvailability } from '../../helper/getGlycerinAvailability/getGlycerinAvailability';
import { getGlycerinOrigin } from '../../helper/getGlycerinOrigin/getGlycerinOrigin';

import { buildGlycerinCartProduct } from '../../../helpers/buildGlycerinObject/buildGlycerinObject';
import { getGlycerinServices } from '../../helper/getGlycerinServices';
import type { GetGlycerinServicesProps } from '../../helper/getGlycerinServices';
import { getAddToBasketColorString } from './getAddToBasketColorString';
import { getAddToBasketVariantString } from './getAddToBasketVariantString';
import type { AddToBasketTrackingParameter } from './types';

/* GraphQL */ `
  fragment TrackGlycerinAddToCartLineItem on BasketLineItem {
    extra {
      promotion
    }
    product {
      sku
      orderNumber
      name
      variation
    }
  }
`;

/* GraphQL */ `
  query BreadcrumbBasketData($categoryId: ID!, $locale: String!) {
    breadcrumb(category: $categoryId, locale: $locale) {
      breadCrumb {
        name
      }
      ...GetCategoryDataFragment
    }
  }
`;

type RawProduct = NonNullable<RawProductQuery['product']>;

export type AddToBasketTrackingData = Pick<
  RawProduct,
  | 'akl'
  | 'brand'
  | 'defaultCategory'
  | 'dimensions'
  | 'longDescription'
  | 'marketcode'
  | 'media'
  | 'name'
  | 'orderNumber'
  | 'promotion'
  | 'sku'
  | 'price'
> & {
  availability: Pick<NonNullable<RawProduct['availability']>, 'isOrderable' | 'state' | 'message'>;
};

export type UseAddToBasketTrackingProps = {
  trackingProductData: AddToBasketTrackingData;
  addToBasketTrackingAplus?: GetGlycerinServicesProps['aplus'];
};

export const useAddToBasketTracking = ({
  trackingProductData,
  addToBasketTrackingAplus,
}: UseAddToBasketTrackingProps) => {
  const {
    akl,
    availability,
    brand,
    defaultCategory,
    dimensions,
    longDescription,
    marketcode,
    media,
    name,
    orderNumber,
    price: priceData,
    promotion,
    sku,
  } = trackingProductData;

  const price = unmask<typeof ProductPriceFragmentFragmentDoc>(priceData);

  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;
  const navq = searchParams?.get('nav-q') || null;

  const { category, searchNo } = useHashParameters();
  const { locale } = useIntl();
  const dispatchGTMEvent = useTracking();
  const quantity = useAtomValue(selectedQuantity);

  const categoryId = category || defaultCategory || undefined;

  const [{ data: breadCrumbData }] = useQuery({
    query: BreadcrumbBasketDataDocument,
    variables: { categoryId: categoryId ?? '', locale },
    context: useMemo(() => ({ suspense: false }), []),
    pause: !categoryId,
  });

  const breadcrumbs: string[] = [];
  if (breadCrumbData?.breadcrumb?.breadCrumb) {
    breadCrumbData.breadcrumb.breadCrumb.forEach((item) => {
      breadcrumbs.push(item.name);
    });
  }
  const color = getAddToBasketColorString(dimensions);
  const variant = getAddToBasketVariantString(dimensions);

  // tracking parameters for atb request
  // implementation according to https://empiriecom.atlassian.net/browse/INFORM-2117
  const getTrackingParameter = (): AddToBasketTrackingParameter | undefined => {
    if (navq || searchNo) {
      return {
        source: 'Suche',
      };
    }
    if (categoryId && breadcrumbs) {
      return {
        source: categoryId,
        breadcrumbs,
      };
    }
    return undefined;
  };

  const trackLayerClosed = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_continueShopping',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToBasketClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_toBasket',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToCheckAndOrder = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'view',
      eventCategory: 'ATB_toCheckAndOrder',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackUpdateCart = (
    lineItemData: ChangeProductMutation['changeProduct']['lineItems'],
    updatedLineItemId?: string,
  ) => {
    const fragmentLineItem = getFragmentData(LineItemFragmentFragmentDoc, lineItemData);
    const updatedLineItem = fragmentLineItem.find((item) => item.id === updatedLineItemId);

    if (updatedLineItem) {
      // found the updated line item update glycerin
      const glycerinProduct = buildGlycerinCartProduct(updatedLineItem);
      dispatchGTMEvent<GTMEventGlycerinUpdateCart>({
        event: 'UpdateCart',
        UpdateCartData: {
          updatedProducts: [glycerinProduct],
        },
      });
      return;
    }
    // last item in list if we can't find the updated one (shouldn't happen)
    const glycerinProduct = buildGlycerinCartProduct(fragmentLineItem[fragmentLineItem.length - 1]);
    dispatchGTMEvent<GTMEventGlycerinUpdateCart>({
      event: 'UpdateCart',
      UpdateCartData: {
        updatedProducts: [glycerinProduct],
      },
    });
  };

  // TODO replace getSkuPart by size from data
  const trackAddToCart = ({
    lineItems,
  }: {
    lineItems: AddToBasketMutation['addToBasket']['lineItems'];
  }) => {
    dispatchGTMEvent({
      Merkzettel: 'nein',
      ecommerce: {
        add: {
          products: [
            {
              brand: brand?.name,
              category: navq ? `search:${navq}` : breadcrumbs?.join('/'),
              description: longDescription || undefined,
              dimension12: price.value > 0 ? 'nein' : 'ja',
              dimension15: getSkuPart(sku, 'Size'),
              dimension16: color,
              dimension18: price.saving?.value! > 0 ? 'ja' : 'nein',
              dimension19: promotion || undefined,
              dimension20: availability.message,
              dimension21: brand?.name,
              id: orderNumber,
              imageUrl: media?.[0].url || undefined,
              marketcode: marketcode || undefined,
              name,
              price: price.value / 100,
              quantity,
              sku,
              variant,
            },
          ],
        },
        cart: {
          products: lineItems,
        },
      },
      event: 'EECaddToCart',
    });
  };

  const trackCatalogAddedProduct = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'catalog_addedproduct',
      eventValue: {
        orderNumber: sku.concat(promotion || ''),
        price: price.value,
        size: getSkuPart(sku, 'Size'),
      },
    });
  };

  const trackGlycerinAddToCart = ({
    lineItems,
  }: {
    lineItems: AddToBasketMutation['addToBasket']['lineItems'];
  }) => {
    dispatchGTMEvent<GTMEventGlycerinAddToCart>({
      event: 'AddToCart',
      AddToCartData: {
        addedProducts: [
          {
            ...getGlycerinArticleId({ orderNumber, promotion }),
            productId: akl,
            skuId: sku,
            quantity,
            currency: price.currency,
            displayedPrice: price.value,
            originalPrice: price.valueOld || undefined,
            ...getGlycerinAvailability(availability),
            ...getGlycerinServices({ aplus: addToBasketTrackingAplus, currency: price.currency }),
            ...getGlycerinOrigin(ref),
          },
        ],
        custom: {
          gtmData: {
            products: lineItems.map((item) => {
              const { extra, product } = getFragmentData(
                TrackGlycerinAddToCartLineItemFragmentDoc,
                item,
              );

              const {
                name: lineItemName,
                sku: lineItemSku,
                orderNumber: lineItemOrderNumber,
                variation: lineItemVariation,
              } = product;

              return {
                orderNumber: lineItemOrderNumber,
                productName: lineItemName,
                promotion: extra?.promotion || '',
                size: getSkuPart(lineItemSku, 'Size'),
                variant: lineItemVariation || '',
              };
            }),
          },
        },
        passive: false,
      },
    });
  };

  return {
    getTrackingParameter,
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogAddedProduct,
    trackGlycerinAddToCart,
    trackUpdateCart,
  };
};
